import { jsxs, jsx } from 'react/jsx-runtime';
import { canScrollTo } from '@flex-design-system/fx-utils/element';
import { composeEventHandlers } from '@radix-ui/primitive';
import { Portal, Root, Trigger, Action, Cancel } from '@radix-ui/react-alert-dialog';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { forwardRef, useRef, useState } from 'react';
import usePopperPresence from '../../hooks/usePopperPresence.mjs';
import { usePopperZIndexStyle } from '../../hooks/usePopperZIndexStyle.mjs';
import { usePreventTransitionInteraction } from '../../hooks/usePreventTransitionInteraction.mjs';
import { overlayStyle } from '../../utils/css.mjs';
import { Flex } from '../layout/Flex.mjs';
import { ShakingAnimation } from '../layout/ShakingAnimation.mjs';
import { ScrollArea } from '../scroll-area/ScrollArea.mjs';
import TextBalancer from '../text-balancer/TextBalancer.mjs';
import { useDialogContext, DialogProvider } from './context.mjs';
import { StyledOverlay, StyledContentPositioner, StyledContent, StyledContentInner, StyledBody, StyledViewport, StyledFog, StyledTitle, StyledDescription, StyledFooterRoot, StyledPortalContainer } from './styled.mjs';

const DIALOG_OVERLAY_ATTRIBUTE = "data-flex-dialog-overlay";
const DIALOG_CONTENT_ATTRIBUTE = "data-flex-dialog-content";
const DialogImpl = (props) => {
  const {
    open: openProp,
    defaultOpen,
    onOpenChange,
    children,
    onClose,
    ...rootProps
  } = props;
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  const [portalContainerElement, setPortalContainerElement] = useState(void 0);
  return /* @__PURE__ */ jsx(
    DialogProvider,
    {
      open,
      onOpenChange: setOpen,
      onClose,
      portalContainerElement,
      onPortalContainerElementChange: setPortalContainerElement,
      children: /* @__PURE__ */ jsx(Root, { ...rootProps, open, onOpenChange: setOpen, children })
    }
  );
};
const DialogContent = forwardRef(
  (props, ref) => {
    const {
      children,
      closeOnInteractOutside = true,
      onInteractOutside,
      overlayTheme = "light",
      css,
      className,
      style,
      portalContainer: portalContainerFromProps,
      zIndex,
      ...contentProps
    } = props;
    const role = closeOnInteractOutside ? "dialog" : "alertdialog";
    const {
      onOpenChange,
      portalContainerElement: portalContainerFromContext,
      open,
      onClose
    } = useDialogContext("DialogContent");
    const animationRef = useRef(null);
    const portalContainer = portalContainerFromProps ?? portalContainerFromContext;
    const { ref: presenceRef } = usePopperPresence({
      open,
      onPresenceChange: (isPresent) => !isPresent && onClose?.()
    });
    const preventTransitionInteraction = usePreventTransitionInteraction({
      open
    });
    const composedRef = useComposedRefs(ref, presenceRef);
    const zIndexStyle = usePopperZIndexStyle(open, zIndex);
    return /* @__PURE__ */ jsxs(Portal, { container: portalContainer, children: [
      /* @__PURE__ */ jsx(
        StyledOverlay,
        {
          ...{ [DIALOG_OVERLAY_ATTRIBUTE]: "" },
          className: overlayStyle({ overlayTheme }),
          css: zIndexStyle,
          onClick: composeEventHandlers(onInteractOutside, (e) => {
            if (!closeOnInteractOutside) {
              animationRef.current?.triggerAnimation();
              return;
            }
            if (!e.defaultPrevented) {
              onOpenChange(false);
              return;
            }
          })
        }
      ),
      /* @__PURE__ */ jsx(
        StyledContentPositioner,
        {
          ...{ [DIALOG_CONTENT_ATTRIBUTE]: "" },
          css: zIndexStyle,
          children: /* @__PURE__ */ jsx(
            StyledContent,
            {
              ...contentProps,
              onKeyDownCapture: (e) => {
                preventTransitionInteraction.onKeyDownCapture?.(e);
                contentProps.onKeyDownCapture?.(e);
              },
              ref: composedRef,
              role,
              children: /* @__PURE__ */ jsx(ShakingAnimation, { ref: animationRef, children: /* @__PURE__ */ jsx(
                StyledContentInner,
                {
                  className,
                  style: { ...style, ...preventTransitionInteraction.style },
                  css,
                  children
                }
              ) })
            }
          )
        }
      )
    ] });
  }
);
const DialogBody = forwardRef(
  (props, ref) => {
    const { children, css, className, ...bodyProps } = props;
    const [scrollAreaViewportElement, setScrollAreaViewportElement] = useState(null);
    const canBottomFogVisible = canScrollTo(
      scrollAreaViewportElement,
      "bottom"
    );
    return /* @__PURE__ */ jsx(StyledBody, { ref, ...bodyProps, children: /* @__PURE__ */ jsxs(ScrollArea, { children: [
      /* @__PURE__ */ jsx(
        StyledViewport,
        {
          css,
          className,
          ref: setScrollAreaViewportElement,
          children: /* @__PURE__ */ jsx(Flex, { direction: "column", align: "center", css: { gap: 10 }, children })
        }
      ),
      canBottomFogVisible && /* @__PURE__ */ jsx(StyledFog, { side: "bottom", color: "#FFFFFF", css: { height: 28 } })
    ] }) });
  }
);
const DialogTitle = forwardRef(({ children, ...props }, ref) => {
  return /* @__PURE__ */ jsx(StyledTitle, { ...props, asChild: true, ref, children: /* @__PURE__ */ jsx(TextBalancer, { as: "h2", children }) });
});
const DialogDescription = forwardRef(({ children, ...props }, ref) => {
  return /* @__PURE__ */ jsx(StyledDescription, { ...props, asChild: true, ref, children: /* @__PURE__ */ jsx(TextBalancer, { as: "p", children }) });
});
const DialogFooter = forwardRef(
  (props, ref) => {
    const { children, orientation = "horizontal", ...footerProps } = props;
    return /* @__PURE__ */ jsx(StyledFooterRoot, { ref, orientation, ...footerProps, children });
  }
);
const DialogPortalContainer = forwardRef((props, ref) => {
  const { onPortalContainerElementChange } = useDialogContext(
    "Dialog.PortalContainer"
  );
  const composedRef = useComposedRefs(ref, onPortalContainerElementChange);
  return /* @__PURE__ */ jsx(StyledPortalContainer, { ...props, ref: composedRef });
});
const Dialog = DialogImpl;
Dialog.Content = DialogContent;
Dialog.Trigger = Trigger;
Dialog.Action = Action;
Dialog.Cancel = Cancel;
Dialog.Body = DialogBody;
Dialog.Footer = DialogFooter;
Dialog.Title = DialogTitle;
Dialog.Description = DialogDescription;
Dialog.UnbalancedTitle = StyledTitle;
Dialog.UnbalancedDescription = StyledDescription;
Dialog.PortalContainer = DialogPortalContainer;

export { DIALOG_CONTENT_ATTRIBUTE, DIALOG_OVERLAY_ATTRIBUTE, Dialog as default };
//# sourceMappingURL=Dialog.mjs.map
